import { type AccountAssetData, type AccountAssetOwnerAsset } from '../../../models/asset/asset-types'
import { type BalanceDataGridRow } from '../../../pages/balance/balance-data-grid'

const DELIMITER = '&&'

export function extractForHeaderInitialExposure (accountAssetData: AccountAssetData): Record<string, string> {
  const results: Record<string, string> = {}
  const accountAsset: AccountAssetOwnerAsset[] = accountAssetData.owner_asset

  if (accountAsset != null) {
    const coinInitialBalanceSums: Record<string, number> = {}

    for (let i = 0; i < accountAsset.length; i++) {
      const item = accountAsset[i]
      const { coin } = item
      const initBalance = item.init_amt

      if (Number.isFinite(coinInitialBalanceSums[coin])) {
        coinInitialBalanceSums[coin] += Number(initBalance)
      } else {
        coinInitialBalanceSums[coin] = Number(initBalance)
      }
    }

    Object.keys(coinInitialBalanceSums)
      .forEach((coin) => {
        results[coin] = coinInitialBalanceSums[coin].toString()
      })
  }

  return results
}

export function createAccountBalanceRows (accountAssetData: AccountAssetData): BalanceDataGridRow[] {
  const accountAsset: AccountAssetOwnerAsset[] = accountAssetData.owner_asset

  const uniqueOwnersAccountBalanceSums: Record<string, number> = {}
  const uniqueOwnersPnlUsdSums: Record<string, number> = {}

  for (let i = 0; i < accountAsset.length; i++) {
    const item = accountAsset[i]
    const { owner } = item
    const currPrice = item.curr_price
    const diffPrice = item.diff_price
    const tag = owner

    if (Number.isFinite(uniqueOwnersAccountBalanceSums[tag])) {
      uniqueOwnersAccountBalanceSums[tag] += Number(currPrice)
      uniqueOwnersPnlUsdSums[tag] += Number(diffPrice)
    } else {
      uniqueOwnersAccountBalanceSums[tag] = Number(currPrice)
      uniqueOwnersPnlUsdSums[tag] = Number(diffPrice)
    }
  }

  const accountRows: BalanceDataGridRow[] = Object.keys(uniqueOwnersAccountBalanceSums)
    .map((owner) => {
      return {
        id: owner,
        path: [owner],
        coin: '',
        account: owner,
        account_type: '',
        symbol: '',
        account_balance: '',
        price_usd: '',
        balance_coin: '',
        balance_usd: uniqueOwnersAccountBalanceSums[owner].toString(),
        initial_balance_coin: '',
        pnl_coin: '',
        pnl_usd: uniqueOwnersPnlUsdSums[owner].toString()
      }
    })

  const uniqueOwnerTypeAccountBalanceSums: Record<string, number> = {}
  const uniqueOwnerTypePnlUsdSums: Record<string, number> = {}

  for (let i = 0; i < accountAsset.length; i++) {
    const item = accountAsset[i]
    const { owner } = item
    const currPrice = item.curr_price
    const diffPrice = item.diff_price
    const accountType = item.account_type
    const tag = `${String(owner)}${DELIMITER}${String(accountType)}`

    if (Number.isFinite(uniqueOwnerTypeAccountBalanceSums[tag])) {
      uniqueOwnerTypeAccountBalanceSums[tag] += Number(currPrice)
      uniqueOwnerTypePnlUsdSums[tag] += Number(diffPrice)
    } else {
      uniqueOwnerTypeAccountBalanceSums[tag] = Number(currPrice)
      uniqueOwnerTypePnlUsdSums[tag] = Number(diffPrice)
    }
  }

  const accountTypeRows: BalanceDataGridRow[] = Object.keys(uniqueOwnerTypeAccountBalanceSums)
    .map((ownerAccountType) => {
      const split = ownerAccountType.split(DELIMITER)
      const owner = String(split[0])
      const accountType = String(split[1])

      return {
        id: ownerAccountType,
        path: [owner, accountType],
        coin: '',
        account: '',
        account_type: accountType,
        symbol: '',
        account_balance: '',
        price_usd: '',
        balance_coin: '',
        balance_usd: uniqueOwnerTypeAccountBalanceSums[ownerAccountType].toString(),
        initial_balance_coin: '',
        pnl_coin: '',
        pnl_usd: uniqueOwnerTypePnlUsdSums[ownerAccountType].toString()
      }
    })

  return accountRows
    .concat(accountTypeRows)
    .concat(createAccountTypeCoinRows(accountAssetData))
}

function createAccountTypeCoinRows (accountAssetData: AccountAssetData): BalanceDataGridRow[] {
  const accountAsset: AccountAssetOwnerAsset[] = accountAssetData.owner_asset

  return accountAsset.map(item => {
    return {
      id: `${String(item.owner)}-${String(item.account_type)}-${String(item.coin)}`,
      path: [String(item.owner), String(item.account_type), item.coin],
      coin: item.coin,
      account: '',
      account_type: '',
      account_balance: item.amount,
      price_usd: item.price,
      balance_coin: item.amount,
      balance_usd: item.curr_price,
      initial_balance_coin: item.init_amt,
      pnl_coin: item.difference,
      pnl_usd: item.diff_price
    }
  })
}
